import { useRef, useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import { useNavigate, useBlocker, useParams, useSubmit, useActionData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'

import {
  useEventsTypeOptions,
  useEventOrganizerOptions,
  useEventsStatusTypeOptions,
  useLocationDataOptions,
  getOption,
  createUniqueDeepClone,
} from '../../../../utils'
import { EventStatus, BlankEnum, LoaderData } from '../../../../types'
import {
  setEvent,
  getEventDetails,
  // setEventDetailsDirtyState,
  // isEventDetailsDirty,
} from '../../../../store.slice'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { useContextStep } from '../CreateEvents.Context'
import { EventSchema } from '../../../events/Events.schema'
import Form from '../../../root/components/Form'
import QuillEditor, { QuillEditorRef } from '../../../root/components/QuillEditor'
import { onSubmitData } from '../CreateEvent.utils'
import { submitEvent } from '../CreateEvents.action'

const defaultEvent = (): EventSchema => ({
  id: -1,
  name: '',
  start: '',
  end: '',
  type: '' as unknown as EventSchema['type'],
  location: null,
  description: '',
  status: EventStatus.Draft,
  organizer: '' as unknown as EventSchema['organizer'],
  picture: '',
  abstract_review_end: null,
  abstract_review_start: null,
  abstract_submission_end: null,
  abstract_submission_start: null,
  sponsorship_request_start: null,
  sponsorship_request_end: null,
  _new: true,
})

export default function EventStep1() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const submit = useSubmit()
  const dispatch = useAppDispatch()
  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const quillRef = useRef<QuillEditorRef>(null)

  const [step, setStep] = useContextStep()
  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))
  const typesOptions = useEventsTypeOptions(t)
  const eventOrganizerOptions = useEventOrganizerOptions(t)
  const statusOptions = useEventsStatusTypeOptions(t)
  const locationsOptions = useLocationDataOptions()

  const actionData = useActionData() as LoaderData<ReturnType<typeof submitEvent>> | undefined
  const [disableSubmit, setDisableSubmit] = useState(false)
  useEffect(() => {
    setDisableSubmit(false)
  }, [actionData])

  const { control, getValues, trigger } = useForm<EventSchema>({
    defaultValues: eventDetails.event ?? createUniqueDeepClone(defaultEvent()),
    resolver: zodResolver(EventSchema),
  })

  useBlocker(({ nextLocation }) => {
    // const isDirty = formState.isDirty || Object.keys(formState.dirtyFields).length > 0
    const values = getValues()
    dispatch(setEvent({ data: values, key: eventId }))
    // if (isDirty) {
    //   dispatch(
    //     setEventDetailsDirtyState({
    //       data: isDirty,
    //       key: eventId,
    //       step: 1,
    //     })
    //   )
    // }

    if (!nextLocation.pathname.includes('/admin/events/')) {
      // const isFormDirty = !!eventDetails.isDirty?.some((dirty, index) =>
      //   index === 1 ? false : Boolean(dirty)
      // )
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = getValues()
    const data = {
      ...eventDetails,
      event: {
        ...eventDetails.event,
        ...values,
      },
    }
    dispatch(setEvent({ data: values, key: eventId }))
    onSubmitData(data, eventId, submit, 'event', setDisableSubmit)
  }

  return (
    <Form onSubmit={onSubmitForm}>
      <Stack direction="column" spacing={2}>
        <h1>{t("Détails de l'événement")}</h1>
        <Divider />

        <Controller
          control={control}
          name="name"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label={t("Nom de l'événement")}
              required
              variant="filled"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="type"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Autocomplete
                onChange={(_, value) => field.onChange(value?.value ?? '')}
                onBlur={field.onBlur}
                defaultValue={getOption(typesOptions, null, field.value)!}
                options={typesOptions}
                id="type-autocomplete"
                disablePortal
                disableClearable
                fullWidth
                renderInput={params => (
                  <TextField
                    {...params}
                    name={field.name}
                    disabled={field.disabled}
                    required
                    label={t("Type d'événement")}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    variant="filled"
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="status"
            render={({ field, fieldState }) => (
              <Autocomplete
                onChange={(_, value) => field.onChange(value?.value ?? '')}
                onBlur={field.onBlur}
                defaultValue={getOption(statusOptions, null, field.value) ?? statusOptions[0]}
                options={statusOptions}
                id="status-autocomplete"
                disablePortal
                fullWidth
                disableClearable
                renderInput={params => (
                  <TextField
                    {...params}
                    name={field.name}
                    disabled={field.disabled}
                    label={t('Statut')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    variant="filled"
                  />
                )}
              />
            )}
          />
        </Stack>
        <Controller
          control={control}
          name="organizer"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? '')}
              onBlur={field.onBlur}
              defaultValue={getOption(eventOrganizerOptions, null, field.value)!}
              options={eventOrganizerOptions}
              id="organizer-autocomplete"
              disablePortal
              disableClearable
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  disabled={field.disabled}
                  required
                  label={t('Organisateur')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  variant="filled"
                />
              )}
            />
          )}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="start"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de début')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="end"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de fin')}
                name={field.name}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    variant: 'filled',
                  },
                }}
              />
            )}
          />
        </Stack>
        <Controller
          control={control}
          name="location"
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? '')}
              onBlur={field.onBlur}
              defaultValue={getOption(locationsOptions, null, field.value)}
              options={locationsOptions}
              id="location-autocomplete"
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  disabled={field.disabled}
                  label={t('Lieu')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  variant="filled"
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <QuillEditor
              ref={quillRef}
              name={field.name}
              label={t('Description')}
              defaultValue={field.value}
              onTextChange={() => {
                field.onChange(JSON.stringify(quillRef?.current?.getContents().ops))
              }}
              variant="filled"
            />
          )}
        />
      </Stack>

      <Stack sx={{ minHeight: '3.5rem' }} />
      <Stack
        direction="row"
        padding="0 2rem 0.5rem"
        sx={{
          position: 'fixed',
          zIndex: 1,
          width: '100%',
          bottom: { xs: '7rem', md: '3rem', lg: '2.5rem' },
          left: 0,
          pointerEvents: 'none',
        }}
      >
        <Stack minWidth={{ xs: '0', md: '340px', lg: '400px' }} />
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2, md: 3 }}
          justifyContent="space-between"
          padding="0 1rem"
          flex="2 0 auto"
        >
          <Button
            onClick={event => {
              event.preventDefault()
              if (window.confirm(t('Êtes-vous sûr de vouloir annuler ?'))) navigate('/dashboard')
            }}
            sx={{ pointerEvents: 'all' }}
          >
            {t('Annuler')}
          </Button>
          <Stack direction="row" spacing={{ xs: 1, sm: 2, md: 3 }}>
            <Button
              variant="contained"
              onClick={() => {
                trigger()
                  .then(valid => {
                    if (!valid) {
                      toast.error(t('Veuillez corriger les erreurs'))
                      return
                    }
                    if (step < 2) {
                      setStep(2)
                    }
                    navigate(`/admin/events/${eventId}/sessions`)
                  })
                  .catch(() => {
                    toast.error(t('Une erreur est survenue'))
                  })
              }}
              sx={{ pointerEvents: 'all' }}
            >
              {t('Suivant')}
            </Button>
            <Button
              disabled={step < 5}
              loading={disableSubmit}
              variant="contained"
              type="submit"
              sx={{ pointerEvents: 'all' }}
            >
              {t('Enregistrer')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Form>
  )
}
